import { ofType, combineEpics } from 'redux-observable';
import { catchError, mergeMap } from 'rxjs/operators';
import { Observable, from, of } from 'rxjs';
import 'rxjs-compat';
import { produceLoadLoadingLoadedEpic } from '@ane/core/lib/utils/ReduxObservablesEpicUtils';
import mdsDataplaneResource from '@ane/exp-data/lib/resources/MdsDataplaneResource';
import helpResource from '@ane/exp-data/lib/resources/HelpResource';
import HelpV2Resource from '@ane/exp-data/lib/resources/HelpV2Resource';
import featureResource from '@ane/exp-data/lib/resources/FeatureResources';
import HelpActions from '../ducks/HelpActions';
import helpActionTypes from '../ducks/HelpActionTypes';
import ticketCategories from '../components/Ticket/TicketCategories';
import Logger from '@ane/core/lib/services/logging/Logger';
import userResources from '@ane/exp-data/lib/resources/UserResources';
import { MasterDataUtilInstance } from '@ane/exp-data/lib/utils/MasterDataUtils';
import EnvironmentService from '@ane/core/lib/services/EnvironmentService';
import { getExperimentationGroupIdFromPath } from '@ane/exp-data/lib/utils/ManagementGroup/ManagementGroup.utils';
import { AsyncRequestStatus } from '@ane/core/lib/models';
import { loadResourcesUtils } from '@ane/core/lib/utils/LoadResourcesUtils';
import { HELP_EVENT_TYPES } from '../../constants';
export const ticketEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.CREATE_TICKET), mergeMap((value) => {
        const fileNewTicketStartTime = performance.now();
        let createdTicket;
        if (EnvironmentService.enableHelpImagePreview()) {
            createdTicket = from(HelpV2Resource.callNewTicketAPI(value.payload)).pipe(mergeMap((r) => {
                const fileNewTicketTime = performance.now() - fileNewTicketStartTime;
                Logger.log(HELP_EVENT_TYPES.TicketCreationTime, {
                    payload: JSON.stringify(value.payload),
                    fileNewTicketTime: fileNewTicketTime,
                });
                return of(HelpActions.createdTicket(r.getSuccessData()));
            }), catchError((error) => of(HelpActions.createdTicket({ Error: true }))));
        }
        else {
            createdTicket = from(helpResource.callNewTicketAPI(value.payload)).pipe(mergeMap((r) => {
                const fileNewTicketTime = performance.now() - fileNewTicketStartTime;
                Logger.log(HELP_EVENT_TYPES.TicketCreationTime, {
                    payload: JSON.stringify(value.payload),
                    fileNewTicketTime: fileNewTicketTime,
                });
                return of(HelpActions.createdTicket(r.getSuccessData()));
            }), catchError((error) => of(HelpActions.createdTicket({ Error: true }))));
        }
        const act = HelpActions.creatingTicket();
        return from([createdTicket, from([act])]).pipe(mergeMap((x) => x));
    }));
};
const uploadUserFileEpicArgs = {
    filterOnType: helpActionTypes.UPLOAD_USER_FILE,
    loadResource: (payload, state) => {
        return helpResource.uploadUserFiles(payload);
    },
    loadingAction: HelpActions.uploadingUserFiles,
    loadedAction: (successData, payload, error) => {
        return of(HelpActions.uploadedUserFiles(successData));
    },
};
export const uploadUserFileEpic = produceLoadLoadingLoadedEpic(uploadUserFileEpicArgs);
export const loadMgmtGroupEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.LOAD_MGMT_GRPS), mergeMap((value) => {
        const managementGroupsLoadStartTime = performance.now();
        const loadManagementGroups = from(mdsDataplaneResource.getPaths(value.payload)).pipe(mergeMap((r) => {
            const managementGroupsLoadTime = performance.now() - managementGroupsLoadStartTime;
            Logger.log(HELP_EVENT_TYPES.ManagementGroupsLoadTime, {
                managementGroupsLoadTime: managementGroupsLoadTime,
            });
            return of(HelpActions.loadedManagementGroups(r));
        }));
        const act = HelpActions.loadingManagementGroups();
        return from([loadManagementGroups, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const loadExperimentationGroupsEpicReducerArgs = {
    actionPrefix: 'help/LOAD_EXPERIMENTATION_GROUPS',
    reduxKeyNested: () => ['experimentationGroups'],
    loadResource: (inputPayload) => mdsDataplaneResource.getExperimentationGroups(inputPayload.workspaceId),
    onCompleteSuccess: (resourceResult) => resourceResult,
};
export const loadExperimentationGroupsEpicReducer = loadResourcesUtils(loadExperimentationGroupsEpicReducerArgs);
export const getFlightEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.LOAD_FLIGHT_DATA), mergeMap((value) => {
        const flightGetStartTime = performance.now();
        const getFlight = from(featureResource.getFlight(value.payload.flightName, value.payload.managementGroup)).pipe(mergeMap((r) => {
            var _a, _b;
            const flightGetTime = performance.now() - flightGetStartTime;
            Logger.log(HELP_EVENT_TYPES.FlightGetTime, {
                payload: JSON.stringify(value.payload),
                flightGetTime: flightGetTime,
            });
            const outputPayloadAsync = r.isSuccessful()
                ? {
                    data: r.getSuccessData(),
                    status: AsyncRequestStatus.Completed,
                    error: undefined,
                    errorContext: undefined,
                }
                : {
                    data: null,
                    status: AsyncRequestStatus.Failed,
                    error: (_b = (_a = r === null || r === void 0 ? void 0 : r.errorContext) === null || _a === void 0 ? void 0 : _a.displayError) !== null && _b !== void 0 ? _b : 'No error information is present',
                    errorContext: r.errorContext,
                };
            return of(HelpActions.loadedFlightData(outputPayloadAsync));
        }));
        const act = HelpActions.loadingFlightData();
        return from([getFlight, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const deleteFlightEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.DELETE_FLIGHT), mergeMap((value) => {
        const flightDeleteStartTime = performance.now();
        const deleteFlight = from(featureResource.tryDeleteFlight(value.payload.flightName, value.payload.managementGroup)).pipe(mergeMap((r) => {
            const flightDeleteTime = performance.now() - flightDeleteStartTime;
            Logger.log(HELP_EVENT_TYPES.FlightDeleteTime, {
                payload: JSON.stringify(value.payload),
                flightDeleteTime: flightDeleteTime,
            });
            return of(HelpActions.deletedFlight(r.getSuccessData()));
        }), catchError((error) => of(HelpActions.deletedFlight({ Error: true }))));
        const act = HelpActions.deletingFlight();
        return from([deleteFlight, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const loadFlightControlEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.LOAD_MDS_FLIGHT_CONTROL), mergeMap((value) => {
        const mdsFlightControlStartTime = performance.now();
        const loadFlightControl = from(mdsDataplaneResource.loadFlightControl(value.payload.workspaceId, getExperimentationGroupIdFromPath(value.payload.workspaceId, value.payload.managementGroup))).pipe(mergeMap((r) => {
            const mdsFlightControlTime = performance.now() - mdsFlightControlStartTime;
            Logger.log(HELP_EVENT_TYPES.FlightControlGetTime, {
                payload: JSON.stringify(value.payload),
                mdsFlightControlGetTime: mdsFlightControlTime,
            });
            return of(HelpActions.loadedFlightControl(r));
        }));
        const act = HelpActions.loadingFlightControl();
        return from([loadFlightControl, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const changeManagementGroupEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.CHANGE_MG), mergeMap((value) => {
        const changeMGStartTime = performance.now();
        const userData = {
            UserName: value.payload.alias,
            ManagementGroup: value.payload.managementGroup,
            Tenant: '', // Note: this field is not used in write call
            //TODO: hardcoded to msft workspace for now. Will evaluate if we need to make it full workspace aware later on.
            WorkspaceId: EnvironmentService.getWorkspaceIdOrDefault(),
        };
        const changeMG = from(userResources.updateUserManagementGroupV2(userData)).pipe(mergeMap((r) => {
            const changeMGTime = performance.now() - changeMGStartTime;
            Logger.log(HELP_EVENT_TYPES.ChangeManagementGroupTime, {
                payload: JSON.stringify(value.payload),
                changeManagementGroupTime: changeMGTime,
            });
            return of(HelpActions.changedManagementGroup(r));
        }));
        const act = HelpActions.changingManagementGroup();
        return from([changeMG, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const loadCategoriesEpic = (action$) => {
    return action$.pipe(ofType(helpActionTypes.LOAD_CATEGORIES), mergeMap((value) => {
        if (!value.payload.managementGroup) {
            return Observable.of({
                type: helpActionTypes.LOADED_CATEGORIES,
                payload: ticketCategories,
            });
        }
        const getCategories = from(mdsDataplaneResource.loadTicketCategories(value.payload.workspaceId, getExperimentationGroupIdFromPath(value.payload.workspaceId, value.payload.managementGroup))).pipe(mergeMap((r) => {
            const transformedCategories = MasterDataUtilInstance.transformTicketCategoriesToComboboxOptions(r);
            if (transformedCategories && transformedCategories.length > 0) {
                return of(HelpActions.loadedCategories(transformedCategories));
            }
            else {
                return of({
                    type: helpActionTypes.LOADED_CATEGORIES,
                    payload: ticketCategories,
                });
            }
        }), catchError((error) => of({
            type: helpActionTypes.LOADED_CATEGORIES,
            payload: ticketCategories,
        })));
        const act = HelpActions.loadingCategories();
        return from([getCategories, from([act])]).pipe(mergeMap((x) => x));
    }));
};
export const helpEpics = combineEpics(ticketEpic, loadMgmtGroupEpic, getFlightEpic, deleteFlightEpic, loadFlightControlEpic, changeManagementGroupEpic, loadCategoriesEpic, uploadUserFileEpic, loadExperimentationGroupsEpicReducer.epic);

//# sourceMappingURL=HelpEpics.js.map
